import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, Clock, CheckCircle } from "lucide-react";

interface EmailVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  isVerified: boolean;
  onVerificationUpdate: () => void;
}

export function EmailVerificationDialog({
  isOpen,
  onClose,
  userEmail,
  isVerified,
  onVerificationUpdate,
}: EmailVerificationDialogProps) {
  const [isResending, setIsResending] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const { toast } = useToast();

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      // Ensure we have a valid session before making the request
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Please sign in again to send verification email");
      }

      const { error } = await supabase.functions.invoke('verify-contact', {
        body: {
          type: 'email',
          action: 'request',
          email: userEmail,
        },
      });

      if (error) throw error;

      toast({
        title: "Verification email sent",
        description: "We've sent a 6-digit code to your email. It expires in 10 minutes.",
      });
    } catch (error: any) {
      toast({
        title: "Error sending verification email",
        description: error.message || 'Please try again in a minute.',
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  // Auto-send verification code when dialog opens for unverified emails
  useEffect(() => {
    if (isOpen && !isVerified && !hasAutoSent) {
      setHasAutoSent(true);
      handleResendVerification();
    }
  }, [isOpen, isVerified, hasAutoSent]);

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      toast({
        title: "Code required",
        description: "Please enter the verification code from your email.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      // Ensure we have a valid session before making the request
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Please sign in again to verify your email");
      }

      const { data, error } = await supabase.functions.invoke('verify-contact', {
        body: {
          type: 'email',
          action: 'verify',
          code: verificationCode.trim(),
        },
      });

      if (error) throw error;

      toast({
        title: "Email verified successfully!",
        description: "You can now create job requests.",
      });
      
      onVerificationUpdate();
      onClose();
    } catch (error: any) {
      toast({
        title: "Invalid verification code",
        description: error.message || "Please check the code and try again, or request a new verification email.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Verification
          </DialogTitle>
          <DialogDescription>
            Manage your email verification status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted">
            {isVerified ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <Clock className="h-5 w-5 text-orange-500" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">
                {userEmail}
              </p>
              <p className="text-xs text-muted-foreground">
                {isVerified ? "Verified" : "Pending verification"}
              </p>
            </div>
          </div>

          {isVerified ? (
            <div className="text-center p-4">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
              <h3 className="font-medium text-green-600">Email Verified!</h3>
              <p className="text-sm text-muted-foreground">
                Your email address has been successfully verified. You can now create job requests.
              </p>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="p-4">
                <Mail className="h-12 w-12 text-orange-500 mx-auto mb-2" />
                <h3 className="font-medium text-orange-600">Verification Required</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Enter the verification code from your email, or request a new verification link.
                </p>
                
                {/* Verification Code Input */}
                <div className="space-y-3">
                  <div className="text-left">
                    <Label htmlFor="verification-code" className="text-sm font-medium">
                      Verification Code
                    </Label>
                    <Input
                      id="verification-code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="mt-1 text-center tracking-wider"
                      maxLength={6}
                    />
                  </div>
                  
                  <Button
                    onClick={handleVerifyCode}
                    disabled={isVerifying || !verificationCode.trim()}
                    className="w-full"
                  >
                    {isVerifying ? "Verifying..." : "Verify Email"}
                  </Button>
                </div>
                
                <div className="my-4 flex items-center">
                  <div className="flex-1 border-t border-border"></div>
                  <span className="px-3 text-xs text-muted-foreground bg-background">or</span>
                  <div className="flex-1 border-t border-border"></div>
                </div>
              </div>

              <Button
                onClick={handleResendVerification}
                disabled={isResending}
                variant="outline"
                className="w-full"
              >
                {isResending ? "Sending..." : "Send Verification Code"}
              </Button>

              <p className="text-xs text-muted-foreground">
                Didn't receive the email? Check your spam folder or click the button above to resend.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}